import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { analyticsService } from '@/services/analytics';
import type {
  AnalyticsData,
  TimeSeriesData,
  OrganizationOverview,
  AnalyticsPeriod,
  DateRange,
  Filters,
  FilterKey,
} from '@/types';

interface AnalyticsState {
  // Site analytics
  siteAnalytics: AnalyticsData | null;
  timeSeries: TimeSeriesData[];
  comparison: {
    pageviews_change: number;
    visitors_change: number;
    views_per_visit_change: number;
  } | null;
  realtimeVisitors: number;
  realtimeCrawlers: number;
  realtimeBots: number;
  period: AnalyticsPeriod;
  customDateRange: DateRange | null;

  // Filters
  filters: Filters;

  // Organization overview
  organizationOverview: OrganizationOverview | null;

  isLoading: boolean;
  error: string | null;

  fetchSiteAnalytics: (siteId: string, period?: AnalyticsPeriod) => Promise<void>;
  fetchOrganizationOverview: (orgId: string, period?: AnalyticsPeriod) => Promise<void>;
  fetchRealtimeVisitors: (siteId: string) => Promise<void>;
  setPeriod: (period: AnalyticsPeriod) => void;
  setCustomDateRange: (range: DateRange) => void;
  setFilter: (key: FilterKey, value: string) => void;
  removeFilter: (key: FilterKey) => void;
  clearFilters: () => void;
  clearAnalytics: () => void;
  clearError: () => void;
}

export const useAnalyticsStore = create<AnalyticsState>()(
  persist(
    (set, get) => ({
      siteAnalytics: null,
      timeSeries: [],
      comparison: null,
      realtimeVisitors: 0,
      realtimeCrawlers: 0,
      realtimeBots: 0,
      period: '30d',
      customDateRange: null,
      filters: {},
      organizationOverview: null,
      isLoading: false,
      error: null,

      fetchSiteAnalytics: async (siteId: string, period?: AnalyticsPeriod) => {
        const currentPeriod = period || get().period;
        const { customDateRange, filters } = get();
        set({ isLoading: true, error: null });

        try {
          const response = await analyticsService.getSiteAnalytics(
            siteId,
            currentPeriod,
            currentPeriod === 'custom' ? customDateRange : null,
            filters,
          );
          set({
            siteAnalytics: response.analytics,
            timeSeries: response.time_series,
            comparison: response.comparison,
            realtimeVisitors: response.realtime_visitors,
            realtimeCrawlers: response.realtime_crawlers ?? 0,
            realtimeBots: response.realtime_bots ?? 0,
            isLoading: false,
          });
        } catch (error) {
          set({ error: 'Failed to load analytics', isLoading: false });
        }
      },

      fetchOrganizationOverview: async (orgId: string, period?: AnalyticsPeriod) => {
        const currentPeriod = period || get().period;
        set({ isLoading: true, error: null });

        try {
          const overview = await analyticsService.getOrganizationOverview(orgId, currentPeriod);
          set({
            organizationOverview: overview,
            isLoading: false,
          });
        } catch (error) {
          set({ error: 'Failed to load organization overview', isLoading: false });
        }
      },

      fetchRealtimeVisitors: async (siteId: string) => {
        try {
          const { visitors, crawlers, bots } = await analyticsService.getRealtimeVisitors(siteId);
          set({ realtimeVisitors: visitors, realtimeCrawlers: crawlers, realtimeBots: bots });
        } catch (error) {
          // Silently fail for realtime - not critical
          console.error('Failed to fetch realtime visitors:', error);
        }
      },

      setPeriod: (period: AnalyticsPeriod) => {
        if (period !== 'custom') {
          set({ period, customDateRange: null });
        } else {
          set({ period });
        }
      },

      setCustomDateRange: (range: DateRange) => {
        set({ period: 'custom' as AnalyticsPeriod, customDateRange: range });
      },

      setFilter: (key: FilterKey, value: string) => {
        const filters = { ...get().filters, [key]: value };
        set({ filters });
      },

      removeFilter: (key: FilterKey) => {
        const filters = { ...get().filters };
        delete filters[key];
        set({ filters });
      },

      clearFilters: () => {
        set({ filters: {} });
      },

      clearAnalytics: () => {
        set({
          siteAnalytics: null,
          timeSeries: [],
          comparison: null,
          realtimeVisitors: 0,
          realtimeCrawlers: 0,
          realtimeBots: 0,
          organizationOverview: null,
        });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'analytics-storage',
      partialize: (state) => ({ period: state.period }),
    }
  )
);

export default useAnalyticsStore;
